import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationsApi } from '../services/notificationsApi';
import { scheduleAligned } from '../utils/pollSchedule';
import { isBackendReachable, subscribeConnectionHealth } from '../services/connectionHealth';

// Cheap badge poll: the list itself is loaded on demand (popover open / Settings page),
// so only the unseen-count is polled. Cadence AND phase both come from the shared poll
// schedule now (#697) — `POLL_SCHEDULE.notifications` — so this poller cannot collide
// with the heartbeat or the review-activity probe. It is 30s, unchanged.
const SEEN_DEBOUNCE_MS = 150;

/**
 * useNotifications — the single data seam for the bell + Settings page (#259).
 *
 * - `unseenCount` is polled cheaply (GET /notifications/unseen-count).
 * - `load(params)` fetches the list (own receipts joined to notifications).
 * - `queueSeen(id)` batches rows that scrolled into view and flushes them to
 *   POST /notifications/seen (per-row IntersectionObserver drives this).
 * - `markViewed(id)` opens detail (GET /notifications/{id} marks it viewed).
 * - `act(id, action)` sets the terminal / close state.
 *
 * Local state is updated optimistically so the badge + rows feel instant; the
 * next poll / reload reconciles with the server.
 */
export function useNotifications({ poll = false } = {}) {
  const [unseenCount, setUnseenCount] = useState(0);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pending = useRef(new Set());
  const flushTimer = useRef(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // `force` bypasses the health gate for USER-DRIVEN refreshes (focus/visibility, an
  // explicit reload). The gate exists to stop an unattended timer flooding a dead
  // backend; a person who just came back to the tab is allowed one request, and if the
  // backend really is down that request fails once and stops there rather than repeating.
  const refreshCount = useCallback(async ({ force = false } = {}) => {
    // HEALTH GATE (owner ask, 2026-07-28) — "review feedback and notifications (and any
    // other repeating poll checks) should always be gated on health so that we don't just
    // sit spamming a broken server/connection." The badge poll is unattended and
    // indefinite, so on an unreachable backend it is pure noise: nobody is waiting on an
    // unseen-count that cannot be fetched, and the number on screen is already the last
    // one we knew. The heartbeat continues (it discovers recovery) and this resumes on
    // the transition — see the poll effect below.
    if (!force && !isBackendReachable()) return;
    try {
      const r = await notificationsApi.unseenCount();
      if (mounted.current) setUnseenCount(r?.unseen_count || 0);
    } catch {
      /* transient poll error — the next tick corrects it */
    }
  }, []);

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const r = await notificationsApi.list(params);
      if (mounted.current) {
        setItems(r?.notifications || []);
        setNextCursor(r?.next_cursor || null);
        if (typeof r?.unseen_count === 'number') setUnseenCount(r.unseen_count);
      }
      return r;
    } catch (e) {
      if (mounted.current) setError(e);
      throw e;
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  const _applySeen = useCallback((ids) => {
    setItems((cur) => cur.map((n) => (
      ids.includes(n.id) && n.state === 'unseen' ? { ...n, state: 'seen' } : n)));
  }, []);

  const flushSeen = useCallback(async () => {
    const ids = [...pending.current];
    pending.current.clear();
    if (!ids.length) return;
    _applySeen(ids);
    try {
      const r = await notificationsApi.seen(ids);
      if (mounted.current && typeof r?.unseen_count === 'number') setUnseenCount(r.unseen_count);
    } catch {
      /* optimistic state stands; a reload/poll reconciles */
    }
  }, [_applySeen]);

  const queueSeen = useCallback((id) => {
    if (!id) return;
    pending.current.add(id);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushSeen, SEEN_DEBOUNCE_MS);
  }, [flushSeen]);

  const markSeen = useCallback(async (ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    if (!list.length) return;
    _applySeen(list);
    try {
      const r = await notificationsApi.seen(list);
      if (mounted.current && typeof r?.unseen_count === 'number') setUnseenCount(r.unseen_count);
    } catch {
      /* ignore */
    }
  }, [_applySeen]);

  const markViewed = useCallback(async (id) => {
    // GET marks the receipt viewed server-side.
    const n = await notificationsApi.get(id);
    if (mounted.current && n) {
      setItems((cur) => cur.map((x) => (x.id === id ? { ...x, ...n } : x)));
      // #401: unseen → viewed clears one from the badge. (Was `Math.max(0, c)`,
      // a no-op that never decremented, so viewing a notification left the dot up
      // until the next 30s poll — contributing to the "badge is unreliable" report.)
      setUnseenCount((c) => (n.state && n.state !== 'unseen' ? Math.max(0, c - 1) : c));
    }
    return n;
  }, []);

  const act = useCallback(async (id, action) => {
    const r = await notificationsApi.action(id, action);
    if (mounted.current && r) {
      setItems((cur) => cur.map((x) => (x.id === id ? { ...x, state: r.state } : x)));
      if (typeof r.unseen_count === 'number') setUnseenCount(r.unseen_count);
    }
    return r;
  }, []);

  useEffect(() => {
    if (!poll) return undefined;
    refreshCount();
    // #697: on the shared wall-clock grid rather than a mount-time interval, so this
    // badge poll never lands in the same tick as the heartbeat or the review-activity
    // probe. Cadence is unchanged at 30s; what is new is that the PHASE is defined at
    // all — a mount-time interval put it wherever the component happened to render.
    //
    // Hidden tabs don't poll (mirrors reviewActivityPoller's gate) — a backgrounded tab
    // has no one looking at the badge, so ticking it is pure server load. `onWake` below
    // already force-refreshes the instant the tab becomes visible again, so this costs no
    // staleness a user would ever see.
    const isVisible = () => typeof document === 'undefined'
      || document.visibilityState !== 'hidden';
    const t = scheduleAligned('notifications',
      () => (isVisible() ? refreshCount() : undefined), { minGapMs: 2000 });
    // #401: the 30s poll alone left the badge stale for up to 30s — on a fresh load
    // (the mount fetch can lose the race with auth or hit a cold-started backend and
    // get silently swallowed) and after a notification arrives between ticks. Refetch
    // whenever the tab regains focus / becomes visible so the dot self-corrects on the
    // very next interaction instead of on the next slow poll.
    const onWake = () => {
      // `force`: a person just came back to the tab. That is an interaction, not an
      // unattended timer, and the gate is about unattended timers.
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') refreshCount({ force: true });
    };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    // Resume on the TRANSITION back to connected, so the badge is not stale for up to
    // 30s after recovery. Transition-only, not "while connected": connectionHealth emits
    // on every successful ping, so reacting to the status alone would add a badge fetch
    // every 15s on a healthy connection — a request storm introduced by the fix for a
    // request storm.
    let wasReachable = isBackendReachable();
    const cancelHealth = subscribeConnectionHealth(({ status }) => {
      const nowReachable = status === 'connected';
      const recovered = nowReachable && !wasReachable;
      wasReachable = nowReachable;
      if (recovered) refreshCount();
    });
    return () => {
      t();
      cancelHealth();
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [poll, refreshCount]);

  return {
    unseenCount, items, nextCursor, loading, error,
    load, refreshCount, queueSeen, markSeen, markViewed, act,
    setItems, setUnseenCount,
  };
}

export default useNotifications;
